<script setup lang="ts">
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import {
  type EditProfileViewKollegeUserFragment,
  useEditProfileViewUpdateKollegeUserMutation,
  useEditProfileViewUpdateKollegeUserProfileAndAddressMutation,
} from './EditProfileView.generated'
import { computed, ref } from 'vue'
import { useAlert } from '@/libs/alertPortal'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import { format } from 'date-fns'

const props = defineProps<{
  user: EditProfileViewKollegeUserFragment
}>()

const savedProfile = computed(() => props.user.profile)
const savedAddress = computed(() => props.user.address)

const profile = ref({
  familyName: savedProfile.value?.familyName || '',
  givenName: savedProfile.value?.givenName || '',
  familyNameKana: savedProfile.value?.familyNameKana || '',
  givenNameKana: savedProfile.value?.givenNameKana || '',
  phoneNumber: savedProfile.value?.phoneNumber || '',
})

const address = ref({
  postalCode: savedAddress.value?.postalCode || '',
  prefecture: savedAddress.value?.prefecture || '',
  city: savedAddress.value?.city || '',
  town: savedAddress.value?.town || '',
  building: savedAddress.value?.building || '',
})

const userEmail = computed(() => props.user.email?.email || '')
const emailActivated = computed(() => props.user.email?.isActivated ?? false)

const birthDate = computed(() => props.user.profile?.birthDate || '')
const formattedBirthDate = computed(() => {
  if (!birthDate.value) return ''
  return format(new Date(birthDate.value), 'yyyy年M月d日')
})

const phoneNumber = computed({
  get: () => profile.value?.phoneNumber || '',
  set: val => {
    if (profile.value) {
      profile.value.phoneNumber = val
    }
  },
})

const { mutate: updateUserMutation } =
  useEditProfileViewUpdateKollegeUserMutation()
const { mutate: updateProfileAndAddressMutation } =
  useEditProfileViewUpdateKollegeUserProfileAndAddressMutation()
const alertPortal = useAlert()

const id = props.user.id
// 反社チェック内容の修正後の値
// nullはちゃんと表示できないので、nullの場合だけ調査中にする
const isAntiSocialEdited = ref(
  props.user.isAntiSocial ?? ('investigating' as const)
)

const handleSaveAntiSocial = async () => {
  const result = await updateUserMutation({
    id,
    isAntiSocial:
      isAntiSocialEdited.value === 'investigating'
        ? null
        : isAntiSocialEdited.value,
  })
  if (result?.errors) {
    alertPortal.error(result?.errors[0]?.message)
    return
  }

  isAntiSocialEdited.value =
    result?.data?.updateKollegeUser?.isAntiSocial ?? 'investigating'

  alertPortal.success('反社チェック情報を更新しました')
}

const handleSaveProfileAndAddress = async () => {
  const result = await updateProfileAndAddressMutation({
    input: {
      id: id,
      profile: {
        familyName: profile.value.familyName,
        givenName: profile.value.givenName,
        familyNameKana: profile.value.familyNameKana,
        givenNameKana: profile.value.givenNameKana,
        phoneNumber: profile.value.phoneNumber,
      },
      address: {
        postalCode: address.value.postalCode,
        prefecture: address.value.prefecture,
        city: address.value.city,
        town: address.value.town,
        building: address.value.building,
      },
    },
  })

  if (result?.errors) {
    alertPortal.error(result?.errors[0]?.message)
    return
  }

  alertPortal.success('プロフィールの変更を保存しました')
  return
}
</script>

<template>
  <section :class="$style.main">
    <SectionTitle :class="$style.title">アカウント編集</SectionTitle>
    <form :class="$style.form" @submit.prevent="handleSaveProfileAndAddress">
      <div :class="$style.form__base">
        <div :class="$style.form__row">
          <div :class="$style.form__rowPart">
            <label :class="$style.label">姓</label>
            <DolphinTextInput
              v-model="profile.familyName"
              :class="$style.input"
            />
          </div>
          <div :class="$style.form__rowPart">
            <label :class="$style.label">名</label>
            <DolphinTextInput
              v-model="profile.givenName"
              :class="$style.input"
            />
          </div>
        </div>
        <div :class="$style.form__row">
          <div :class="$style.form__rowPart">
            <label :class="$style.label">セイ</label>
            <DolphinTextInput
              v-model="profile.familyNameKana"
              :class="$style.input"
            />
          </div>
          <div :class="$style.form__rowPart">
            <label :class="$style.label">メイ</label>
            <DolphinTextInput
              v-model="profile.givenNameKana"
              :class="$style.input"
            />
          </div>
        </div>

        <div :class="$style.form__row">
          <div :class="$style.form__rowPart">
            <label :class="$style.label">メールアドレス</label>
            <DolphinTextInput
              v-model="userEmail"
              :class="$style.input"
              disabled
            />
          </div>
          <div :class="$style.form__rowPart">
            <label :class="$style.label">認証状況</label>
            <div :class="$style.info">
              <template v-if="emailActivated"> 認証済み </template>
              <template v-else>認証していません</template>
            </div>
          </div>
        </div>

        <div :class="$style.form__row">
          <div :class="$style.form__rowPart">
            <label :class="$style.label">生年月日</label>
            <DolphinTextInput
              :model-value="formattedBirthDate"
              :class="$style.input"
              disabled
            />
          </div>

          <div :class="$style.form__rowPart">
            <label :class="$style.label">電話番号</label>
            <DolphinTextInput v-model="phoneNumber" :class="$style.input" />
          </div>
        </div>

        <div :class="$style.form__row">
          <div :class="$style.form__rowPart">
            <label :class="$style.label">郵便番号</label>
            <DolphinTextInput
              v-model="address.postalCode"
              :class="$style.input"
            />
          </div>
        </div>

        <div :class="$style.form__row">
          <div :class="$style.form__rowPart">
            <label :class="$style.label">都道府県</label>
            <DolphinTextInput
              v-model="address.prefecture"
              :class="$style.input"
            />
          </div>
          <div :class="$style.form__rowPart">
            <label :class="$style.label">市区町村</label>
            <DolphinTextInput v-model="address.city" :class="$style.input" />
          </div>
        </div>

        <div :class="$style.form__row">
          <div :class="$style.form__rowPart">
            <label :class="$style.label">町名・番地</label>
            <DolphinTextInput v-model="address.town" :class="$style.input" />
          </div>
        </div>

        <div :class="$style.form__row">
          <div :class="$style.form__rowPart">
            <label :class="$style.label">建物名</label>
            <DolphinTextInput
              v-model="address.building"
              :class="$style.input"
            />
          </div>
        </div>
      </div>
      <FlatButton :class="$style.saveButton" type="submit">
        プロフィールの変更を保存
      </FlatButton>
    </form>

    <form :class="$style.form" @submit.prevent="handleSaveAntiSocial">
      <div :class="$style.form__base">
        <div :class="$style.form__part">
          <label :class="$style.label">反社チェック</label>
          <DolphinSelector
            v-model="isAntiSocialEdited"
            :options="[
              { text: '調査中', value: 'investigating' },
              { text: '問題なし', value: false },
              { text: '問題あり', value: true },
            ]"
          />
        </div>
      </div>
      <FlatButton :class="$style.saveButton" type="submit">
        反社チェックの結果を保存
      </FlatButton>
    </form>
  </section>
</template>

<style lang="scss" module>
.title {
  padding-bottom: 24px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.main {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__base {
    display: flex;
    flex-direction: column;
    padding: 16px;
    background-color: $color-surface-secondary;
    border-radius: $border-radius-v2-base;
    gap: 12px;
  }

  &__row {
    display: flex;
    gap: 16px;
    width: 100%;

    & > * {
      flex: 1;

      & > * {
        align-items: stretch;
      }
    }
  }

  &__rowPart {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  &__part {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

.label {
  color: $color-text-and-object-high-emphasis;
  font-size: 12px;
  font-weight: bold;
}

.input {
  padding: 12px;
  line-height: 24px;
}

.info {
  padding: 12px 0;
  line-height: 24px;
}

.saveButton {
  align-self: flex-end;
}
</style>
